import React, { useState } from "react";
import { Badge, Empty, Spin, Tabs } from "antd";
import { useTranslation } from "react-i18next";

import {
  TeamMemberTaskCategory,
  TeamMemberTaskStatus,
  type TeamMemberTaskWithTrainee,
} from "@fitness-app/data-models/entities/TeamMemberTask";

import { useTeamMemberTasks } from "../hooks/useTeamMemberTasks";
import { categoryOptions } from "../types";
import { TeamMemberTaskItem } from "./TeamMemberTaskItem";
import { type TaskListFilters } from "./TeamMemberTaskList";

interface CategoryViewProps {
  filters: TaskListFilters;
}

const categoryOrder: TeamMemberTaskCategory[] = [
  TeamMemberTaskCategory.ClientCommunication,
  TeamMemberTaskCategory.TrainingPlan,
  TeamMemberTaskCategory.NutritionPlan,
  TeamMemberTaskCategory.Measurements,
  TeamMemberTaskCategory.ProgressReview,
  TeamMemberTaskCategory.ProgramRenewals,
  TeamMemberTaskCategory.Other,
];

export const CategoryView: React.FC<CategoryViewProps> = ({ filters }) => {
  const { t } = useTranslation("dashboard");
  const [activeCategory, setActiveCategory] = useState<TeamMemberTaskCategory>(
    TeamMemberTaskCategory.ClientCommunication,
  );

  const { tasks, isLoading } = useTeamMemberTasks({
    showCompleted: filters.showCompleted,
    traineeId: filters.traineeId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spin />
      </div>
    );
  }

  // Filter tasks based on showCompleted
  const filteredTasks = filters.showCompleted
    ? tasks
    : tasks.filter((task) => task.status !== TeamMemberTaskStatus.Completed);

  // Group tasks by category
  const groupedByCategory = filteredTasks.reduce<Record<TeamMemberTaskCategory, TeamMemberTaskWithTrainee[]>>(
    (acc, task) => {
      const category = task.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(task);
      return acc;
    },
    {} as Record<TeamMemberTaskCategory, TeamMemberTaskWithTrainee[]>,
  );

  const getCategoryLabel = (category: TeamMemberTaskCategory): string => {
    return categoryOptions.find((opt) => opt.value === category)?.label ?? category;
  };

  const tabItems = categoryOrder.map((category) => {
    const categoryTasks = groupedByCategory[category] || [];
    const count = categoryTasks.length;

    return {
      key: category,
      label: (
        <div className="flex items-center gap-2">
          <span>{getCategoryLabel(category)}</span>
          {count > 0 && <Badge color="blue" count={count} size="small" />}
        </div>
      ),
      children: (
        <div className="space-y-2">
          {categoryTasks.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("tasks.noTasksInCategory")} />
          ) : (
            categoryTasks.map((task) => <TeamMemberTaskItem key={task.id} task={task} showDate />)
          )}
        </div>
      ),
    };
  });

  return (
    <Tabs
      tabPosition="left"
      activeKey={activeCategory}
      onChange={(key) => setActiveCategory(key as TeamMemberTaskCategory)}
      items={tabItems}
      className="category-view-tabs min-h-[600px]"
    />
  );
};
